/**
 * DemoBetaBanner — slim, always-visible BETA notice that sits above the authed
 * shell while in the Ando demo.
 *
 * Demo-only and intentionally PERSISTENT: the user wants the "this is a beta
 * demo" context on screen at all times, so there's no dismiss. It's rendered as
 * a flow element at the top of the shell column (NOT ``position: fixed``), so it
 * reserves its own height and never covers the workspace underneath.
 *
 * Slim by design: a single line on desktop that wraps cleanly on mobile.
 * Pencil aesthetic — warm paper wash on cream (``bg-cream``) with a hairline
 * divider, the orange living ONLY in the pencil-line BETA pill + the "Scopri di
 * più" link (no full orange block). Info tone, not a harsh warning.
 * ``role="status"`` so assistive tech can surface it without stealing focus.
 *
 * A "Rivedi il tour guidato" affordance (re)launches the guided tour via
 * ``useDemoTourStore.start(onboardingKey())`` — the SAME entry point as the
 * empty-state / settings buttons — so the tour is always one click away.
 */
import { Trans } from "@lingui/react/macro";
import { CalendarClock, Sparkles } from "lucide-react";

import { useDemoBookCallStore } from "./demoBookCallStore";
import { useDemoTourStore } from "./demoTourStore";
import { onboardingKey, useOnboardingMode } from "./useOnboardingMode";

export function DemoBetaBanner(): React.ReactElement | null {
  const isDemo = useOnboardingMode() === "demo";
  const startTour = useDemoTourStore((s) => s.start);
  const openBookCall = useDemoBookCallStore((s) => s.openModal);
  const bookCallCaptured = useDemoBookCallStore((s) => s.captured);

  if (!isDemo) return null;

  return (
    <div
      role="status"
      className="flex w-full shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 border-hairline border-b bg-cream/70 px-4 py-1.5 text-center"
    >
      {/* Beta pill — the "brand pencil line": transparent fill + thin orange
          hairline + warm accent icon. No filled chip (pencil = less boxing). */}
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-ando-orange/45 bg-transparent px-2 py-0.5 font-mono text-[10px] text-ando-orange uppercase tracking-[0.16em]">
        <Sparkles size={10} strokeWidth={2.2} aria-hidden className="text-ando-orange" />
        <Trans>Beta</Trans>
      </span>

      <p className="text-[12.5px] text-ink-2 leading-snug">
        <Trans>
          <strong className="font-medium text-ink-1">Stai esplorando la demo di Ando</strong> ·
          beta con dati simulati. Qualche piccolo intoppo è possibile — riguarda solo la demo, non
          il prodotto, che è già solido e stabile.
        </Trans>
      </p>

      <button
        type="button"
        onClick={() => startTour(onboardingKey())}
        className="shrink-0 rounded-md text-[12.5px] text-ando-orange underline-offset-2 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
      >
        <Trans>Rivedi il tour guidato</Trans>
      </button>

      {/* Always-visible re-open for the "book a call" modal — the soft
          banner may be dismissed, but this pill keeps the CTA one click away
          for the whole session. Hidden after conversion (never re-prompt). */}
      {bookCallCaptured ? null : (
        <button
          type="button"
          onClick={openBookCall}
          className="inline-flex shrink-0 items-center gap-1 rounded-md text-[12.5px] text-ando-orange underline-offset-2 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
        >
          <CalendarClock size={12} strokeWidth={2} aria-hidden />
          <Trans>Prenota demo</Trans>
        </button>
      )}
    </div>
  );
}

DemoBetaBanner.displayName = "DemoBetaBanner";
